import React, { useEffect, useRef, useState } from "react";
import Button from "components/semantic/Button";
import Card from "components/common/Card";
import { useNavigate } from "react-router-dom";
import imageIcon from "assets/icons/image.svg";
import pdfIcon from "assets/icons/pdfImg.svg";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector, useUserDetails } from "utils/hooks";
import { clearData, saveDocList } from "store/slice/kyp.slice";
import imageCompression from "browser-image-compression";
import jsPDF from "jspdf";
import { getUserProfileThunk, uploadPolicyThunk } from "store/thunk/kyp.thunk";
import uploadedIcon from "assets/icons/image-icon.svg";
import deleteIcon from "assets/icons/delete-icon.svg";
import uploadPdfIcon from "assets/icons/pdf.svg";

const uploadOptions = [
  {
    id: "pdf",
    label: "PDF",
    icon: <img src={pdfIcon} className="w-6 md:w-7" />,
    accept: ".pdf"
  },
  {
    id: "image",
    label: "Image",
    icon: <img src={imageIcon} className="w-6 md:w-7" />,
    accept: ".jpg,.jpeg,.png"
  }
];
const InsurancePolicyForm: React.FC = () => {
  const navigate = useNavigate();
  const inputRef: any = useRef();
  const dispatch = useAppDispatch();
  const [partnerId, setPartnerId] = useState<any>("");

  const docList = useAppSelector((state) => state.rootReducer.kypSlice.docList);
  const fileType = useAppSelector((state) => state.rootReducer.kypSlice.uploadedFileType);
  const isLoading = useAppSelector((state) => state.rootReducer.kypSlice.loadingUploadPolicy);

  const handleUpload = (id: string) => {
    if (id === "image") navigate("/kyp/upload-policy/docs/image");
    else if (id === "pdf") {
      if (inputRef.current) {
        inputRef.current?.click();
      }
    }
  };

  useEffect(() => {
    dispatch(getUserProfileThunk())
      .unwrap()
      .then((data) => {
        if (data?.success) {
          setPartnerId(data?.data?.userCreatedBy);
        }
      });
  }, []);

  const currentUserDetails = useUserDetails();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const maxSize = 10 * 1024 * 1024;
    const maxFiles = 10;
    const exceededFiles = files.filter((file) => file.size > maxSize);
    const types = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];

    const fileExtension = files[0].type;
    if (!types.includes(fileExtension)) {
      toast.error("Please upload a PDF, JPG, JPEG, or PNG file.");
      return;
    }
    if (exceededFiles.length > 0) {
      toast.error("One or more files exceed the 10MB limit.");
      return;
    }
    if (files.length > maxFiles) {
      toast.error(`You can only upload a maximum of ${maxFiles} files.`);
      return;
    }

    dispatch(saveDocList(files));
  };

  useEffect(() => {
    if (fileType !== "image") dispatch(saveDocList([]));
  }, []);

  const submitHandler = async () => {
    const formData = new FormData();

    // Compress and add images to FormData
    const compressedFiles = [];

    if (fileType === "image") {
      for (const file of docList) {
        const options = {
          maxSizeMB: 10, // Maximum size in MB
          maxWidthOrHeight: 1920, // Maximum width or height of the image
          useWebWorker: true // Use Web Workers for faster compression
        };
        try {
          const compressedFile = await imageCompression(file, options);
          compressedFiles.push(compressedFile);
        } catch (error) {
          // console.error("Image compression error:", error);
        }
      }
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      await Promise.all(
        compressedFiles.map((image, index) => {
          return new Promise<void>((resolve) => {
            const imgData = URL.createObjectURL(image);
            const img = new Image();
            img.src = imgData;
            img.onload = function () {
              const imgWidth = img.width;
              const imgHeight = img.height;
              const ratio = pageWidth / imgWidth;
              const scaledWidth = pageWidth;
              const scaledHeight = imgHeight * ratio;
              const yPos = (pageHeight - scaledHeight) / 2;

              if (index > 0) {
                doc.addPage();
              }

              doc.addImage(img, "PNG", 0, yPos, scaledWidth, scaledHeight);
              resolve();
            };
          });
        })
      );
      const pdfBlob = doc.output("blob");
      formData.set("file", pdfBlob, "policy_images.pdf");
    } else {
      formData.set("file", docList[0]);
    }

    formData.set("userId", currentUserDetails?._id || "");
    formData.set("source", "kyp");
    formData.set("kypSrc", "web");
    formData.set("owner", currentUserDetails?._id);

    if (partnerId) formData.set("partnerId", partnerId);

    const couponCode = sessionStorage.getItem("coupon");
    if (couponCode) formData.set("couponCode", couponCode);

    dispatch(uploadPolicyThunk(formData))
      .unwrap()
      .then((data) => {
        if (data?.success) {
          dispatch(clearData());
          navigate(`/kyp/scanning-policy/${data?.data?._id}`);
        }
      });
  };

  const deleteDocumentHandler = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    dispatch(saveDocList([]));
  };

  return (
    <div className="max-w-4xl mx-auto ">
      <h1 className="text-lg md:text-2xl font-extrabold text-gray-800 my-4">Policy Details</h1>

      <Card className="w-full">
        <div className="flex justify-center flex-col items-center  border-b-[1px] border-b-border-color mx-3 md:mx-0 ">
          <div className="border-[2px] border-[#00000033] border-dashed p-2 py-6 md:p-6 my-8 rounded-xl  bg-white ">
            <p className="text-[22px] font-extrabold text-center">Upload your Policy</p>
            <p className="font-medium text-gray-500 mt-1 text-center">
              Supported files: <span className="font-semibold text-[#FF6B00]">pdf</span>, <span className="font-semibold text-[#FF6B00]">jpg</span> or <span className="font-semibold text-[#FF6B00]">png</span>
            </p>
            <div className="flex justify-center gap-4 flex-wrap m-6 ">
              {uploadOptions.map((option: any, index: number) => {
                return (
                  <div id={option.id} onClick={() => handleUpload(option.id)} className="border-[1px] cursor-pointer border-border-color rounded-[10px] shadow-lg flex flex-col md:flex-row px-4 w-24 md:w-40 md:px-7 py-3 text-sm font-bold justify-center items-center gap-1 md:gap-3" key={index}>
                    {option.icon}
                    <span className="text-sm font-bold">{option.label}</span>
                  </div>
                );
              })}
              <input className="hidden" accept={".pdf"} ref={inputRef} type="file" onChange={handleFileChange} />
            </div>
            {docList.length > 0 && (
              <div className="px-10">
                <p className="border-b-[1px] border-border-color text-lg font-bold">Uploaded Files</p>
                <div className="flex items-center p-2 justify-between">
                  <div className="flex gap-x-2 items-center">
                    <img src={fileType === "image" ? uploadedIcon : uploadPdfIcon} />
                    <p className="text-xs font-semibold max-w-32 md:max-w-40 truncate pr-3">{fileType === "image" ? "policy_images.pdf" : docList[0]?.name}</p>
                  </div>
                  <img className="cursor-pointer" src={deleteIcon} onClick={deleteDocumentHandler} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-center items-center py-4">
          <Button title="NEXT" onClick={submitHandler} isLoading={isLoading} className={`${docList.length === 0 && "opacity-50 pointer-events-none"}`} />
        </div>
      </Card>
    </div>
  );
};

export default InsurancePolicyForm;
